import { RetryIcon } from "@solomon-labs/design/components/icons";
import { Button, Text } from "@solomon-labs/design/components/ui";
import React from "react";

export interface TermsLoadNoticeProps {
    /** `useRetailTerms().error`. Null while the first request is still
     *  in flight, which is a wait rather than a failure. */
    error: string | null;
    onRefresh: () => void;
}

/**
 * Takes the acceptance checkbox's place when the published terms cannot
 * be loaded. It replaces that row rather than sitting above it: with no
 * document there is nothing to agree to, and the checkbox used to
 * render anyway, offering a link to terms that were not there.
 *
 * The raw failure string is not shown. It was the browser's own
 * `TypeError` with the API host appended, which told the user nothing
 * and leaked the environment into the form.
 *
 * Reachable in `/showcase/enrollment-errors` — it only appears when a
 * request fails, so no `?state=` frame can produce it.
 */
export function TermsLoadNotice({
    error,
    onRefresh
}: TermsLoadNoticeProps): React.ReactElement {
    if (!error) {
        // Still in flight. No retry: there is nothing to retry yet.
        return (
            <div className="mt-6" role="status" aria-live="polite">
                <Text variant="fine-print" className="text-foreground-neutral">
                    Loading Terms…
                </Text>
            </div>
        );
    }
    return (
        <div
            // `mt-6` matches EnableTosCheckbox's own row: this notice takes
            // that row's place, so it has to sit where the checkbox sat.
            // Without it the form jumps 24px when the terms fail to load.
            className="mt-6 flex flex-wrap items-center gap-2"
            role="status"
            aria-live="polite"
        >
            <Text variant="fine-print" className="text-error-base">
                Issue fetching the terms.
            </Text>
            {/* `2xs` resolves to the `fine-print` Text variant, matching
                the message beside it and the destination row above. */}
            <Button
                variant="link"
                size="2xs"
                trailing={<RetryIcon />}
                onClick={onRefresh}
            >
                Retry
            </Button>
        </div>
    );
}
